import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Button from "Components/Common/Button/Button";
import Input from "Components/Common/Input/Input";
import type { ProfileData } from "hooks/useProfile";
import { NAME_REGEX, CHAR_LIMIT } from "utils/Common/Constant";
import type { ProfileUpdatePayload } from "services/profileService";

const Schema = Yup.object({
  firstName: Yup.string()
    .required("First name is required")
    .matches(NAME_REGEX, "Letters only")
    .max(CHAR_LIMIT.NAME, `Max ${CHAR_LIMIT.NAME} characters`),
  lastName: Yup.string()
    .required("Last name is required")
    .matches(NAME_REGEX, "Letters only")
    .max(CHAR_LIMIT.NAME, `Max ${CHAR_LIMIT.NAME} characters`),
  title: Yup.string().max(
    CHAR_LIMIT.SHORT,
    `Max ${CHAR_LIMIT.SHORT} characters`,
  ),
});

interface Props {
  profile: ProfileData;
  onSave: (values: ProfileUpdatePayload) => Promise<boolean>;
  saving: boolean;
}

export default function PersonalInfoSection({
  profile,
  onSave,
  saving,
}: Props) {
  return (
    <Formik
      initialValues={{
        firstName: profile.firstName,
        lastName: profile.lastName,
        title: profile.title,
      }}
      validationSchema={Schema}
      onSubmit={async (values, { resetForm }) => {
        const ok = await onSave(values);
        if (ok) resetForm({ values });
      }}
      enableReinitialize
    >
      {({ resetForm, dirty }) => (
        <Form>
          <div className="pf-card">
            <div className="pf-card__title">Personal Information</div>
            <div className="pf-field-row pf-c2">
              <Field name="firstName">
                {({ field, meta }: any) => (
                  <div className="pf-field">
                    <label>
                      First Name <span className="pf-req">*</span>
                    </label>
                    <Input
                      className={`pf-fi${meta.touched && meta.error ? " error" : ""}`}
                      placeholder="First name"
                      {...field}
                    />
                    {meta.touched && meta.error && (
                      <p className="pf-field-error">{meta.error}</p>
                    )}
                  </div>
                )}
              </Field>
              <Field name="lastName">
                {({ field, meta }: any) => (
                  <div className="pf-field">
                    <label>
                      Last Name <span className="pf-req">*</span>
                    </label>
                    <Input
                      className={`pf-fi${meta.touched && meta.error ? " error" : ""}`}
                      placeholder="Last name"
                      {...field}
                    />
                    {meta.touched && meta.error && (
                      <p className="pf-field-error">{meta.error}</p>
                    )}
                  </div>
                )}
              </Field>
            </div>
            <div className="pf-field-row pf-c2">
              <Field name="title">
                {({ field, meta }: any) => (
                  <div className="pf-field">
                    <label>Title / Role</label>
                    <Input
                      className="pf-fi"
                      placeholder="e.g. Project Coordinator"
                      {...field}
                    />
                    {meta.touched && meta.error && (
                      <p className="pf-field-error">{meta.error}</p>
                    )}
                  </div>
                )}
              </Field>
              <div className="pf-field">
                <label>Organization</label>
                <Input
                  className="pf-fi readonly"
                  value={profile.organization}
                  readOnly
                />
              </div>
            </div>
            <div className="pf-btn-row">
              <Button
                type="submit"
                className="pf-btn-primary"
                disabled={saving || !dirty}
              >
                {saving ? (
                  <>
                    <span className="btn-spinner" /> Saving…
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
              <Button
                type="button"
                className="pf-btn-secondary"
                onClick={() => resetForm()}
                disabled={saving}
              >
                Discard
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
